/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ItemInfo } from './pictoChart.type';
import { useDatatip } from '#hooks/PRIVATE_useDatatip';
import { calculateOffsetForPicto as calculateOffset } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import { ComponentChildren } from 'preact';
import { useUser } from '#hooks/UNSAFE_useUser';
import { Property } from 'csstype';
import { Dimension } from '#utils/UNSAFE_visTypes/common';

type Props = {
  touchResponse: 'touchStart' | 'touchHold';
  content?: ComponentChildren;
  borderColor?: Property.BorderColor;
  focusedItemInfo: ItemInfo;
  width: number;
  getFocusedItemDimension: (item: ItemInfo) => Dimension | undefined;
  defaultContainer?: 'enabled' | 'disabled';
};

/**
 * Returns the datatip.
 * @param content The text string for the picto chart item.
 * @param focusedItemRef The ref for focused item node.
 * @param focusedItemInfo The ItemInfo for focused item node.
 * @returns
 */
export const usePictoChartDatatip = ({
  touchResponse,
  content,
  borderColor,
  width,
  focusedItemInfo,
  getFocusedItemDimension,
  defaultContainer = 'enabled'
}: Props) => {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const { datatipContent, datatipProps } = useDatatip({
    content,
    placement: 'top-start',
    offset: calculateOffset(
      isRtl,
      width,
      focusedItemInfo?.isCurrent ? getFocusedItemDimension(focusedItemInfo) : undefined
    ),
    borderColor,
    anchor: focusedItemInfo?.isCurrent ? 'element' : 'pointer',
    touchResponse,
    defaultContainer
  });
  return { datatipContent: datatipContent, datatipProps: datatipProps };
};
